import React, { useCallback, useEffect, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme
} from '@mui/material'
import hipersendService from '../../services/hipersend'
import Text from '../Text'
import { descreverRecorrencia } from '../../libs/scheduledMessages'

const sm = hipersendService.scheduledMessages

// As cores são semânticas e não têm relação com a cor da marca: verde é o
// caminho feliz, âmbar é "segurado e volta depois", cinza-azulado é "barrado de
// propósito" e vermelho é falha de verdade.
const CORES = {
  queued: '#25d366',
  held: '#ffb300',
  blocked: '#7e96a8',
  failed: '#ef5350'
}

const STATUS_RUN = {
  pending: 'Aguardando',
  processing: 'Em andamento',
  done: 'Concluída',
  failed: 'Falhou',
  canceled: 'Cancelada',
  paused_saldo: 'Pausada por saldo'
}

// Retidas = Filtro de números + falta de saldo. Quando parte é de saldo, a
// célula diz quantas — o cliente precisa saber que recarregar resolve.
function retidas(c) {
  const total = c.held || 0
  const porSaldo = c.heldSaldo || 0
  return porSaldo ? `${total} (${porSaldo} por saldo)` : String(total)
}

function formatarData(iso) {
  if (!iso) return '—'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Sao_Paulo'
  }).format(new Date(iso))
}

function Barra({ contadores, total }) {
  if (!total) return null
  const fatia = (n) => `${((n || 0) / total) * 100}%`
  return (
    <div className="flex h-1.5 rounded overflow-hidden" style={{ minWidth: 110, backgroundColor: '#0002' }}>
      <i style={{ width: fatia(contadores.queued), backgroundColor: CORES.queued }} />
      <i style={{ width: fatia(contadores.held), backgroundColor: CORES.held }} />
      <i style={{ width: fatia(contadores.blocked), backgroundColor: CORES.blocked }} />
      <i style={{ width: fatia(contadores.failed), backgroundColor: CORES.failed }} />
    </div>
  )
}

export default function Historico({ aberto, agendamento, api, secret, onFechar, notify }) {
  const theme = useTheme()
  const [execucoes, setExecucoes] = useState([])
  const [loading, setLoading] = useState(false)

  const carregar = useCallback(async () => {
    if (!agendamento?._id) return
    setLoading(true)
    try {
      setExecucoes(await sm.history(api, secret, agendamento._id))
    } catch (e) {
      notify?.('Falha ao carregar o histórico')
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [api, secret, agendamento, notify])

  useEffect(() => { carregar() }, [carregar])

  return (
    <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="md">
      <DialogTitle>
        <Text component="span" className="font-bold text-lg">{agendamento?.title}</Text>
        <Text className="font-light text-sm">
          {descreverRecorrencia(agendamento?.schedule)}
        </Text>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <div className="flex justify-center py-8"><CircularProgress size={28} /></div>
        ) : !execucoes.length ? (
          <Text className="font-light text-sm py-4">
            Ainda não houve nenhuma execução. A primeira acontece na próxima data programada.
          </Text>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Execução</TableCell>
                    <TableCell>Resultado</TableCell>
                    <TableCell align="right">Na fila</TableCell>
                    <TableCell align="right">Retidas</TableCell>
                    <TableCell align="right">Bloq.</TableCell>
                    <TableCell align="right">Falhas</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {execucoes.map((run) => {
                    const c = run.counters || {}
                    const cancelada = run.status === 'canceled'
                    return (
                      <TableRow key={run._id || run.occurrenceAt}>
                        <TableCell>{formatarData(run.occurrenceAt)}</TableCell>
                        <TableCell>
                          {cancelada ? (
                            <Text component="span" className="text-xs" style={{ color: CORES.held }}>
                              {run.canceledReason || STATUS_RUN.canceled}
                            </Text>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Barra contadores={c} total={c.total} />
                              <Text component="span" className="text-xs opacity-70">
                                {STATUS_RUN[run.status] || run.status}
                              </Text>
                            </div>
                          )}
                        </TableCell>
                        <TableCell align="right">{c.queued || 0}</TableCell>
                        <TableCell align="right">{retidas(c)}</TableCell>
                        <TableCell align="right">{c.blocked || 0}</TableCell>
                        <TableCell align="right">{c.failed || 0}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <div
              className="mt-4 p-3 rounded-lg"
              style={{
                border: `1px solid ${theme.palette.primary.main}44`,
                backgroundColor: `${theme.palette.primary.main}14`
              }}
            >
              <Text className="font-light text-xs">
                <b>Na fila</b> é o que entrou para envio. O resultado final de cada mensagem
                (entregue, lida, erro) fica em <b>Relatórios</b> — é lá que a entrega é
                confirmada. <b>Retidas</b> foram seguradas pelo Filtro de números ou pela falta de saldo e saem
                depois (as de saldo, sozinhas após a recarga); <b>bloqueadas</b> o Filtro barrou de propósito.
              </Text>
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onFechar} sx={{ textTransform: 'none' }}>Fechar</Button>
      </DialogActions>
    </Dialog>
  )
}
